'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { Group, Tree, TreeNodeData, useTree } from '@mantine/core';
import { RoundedSectionCard } from '@/components/common/RoundedSectionCard';
import { Document, DocumentCategory } from '@/actions/dtos/document';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileLines,
  faFolderClosed,
  faChevronRight,
  faChevronDown,
  faEyeSlash,
  faEye,
  faArrowsFromLine,
  faArrowsToLine,
  faCircleInfo,
} from '@fortawesome/pro-duotone-svg-icons';
import { getIconByDocType, formatFileSize, getFileContentTitle } from '@/utils/fileUtils';
import { useHasTenderAccess } from '@/hooks/useHasTenderAccess';
import { useAuthenticated } from '@/hooks/useAuthenticated';
import TenderAccessButton from '../access/TenderAccessButton';
import { groupDocumentsIntoFolders } from '@/utils/documents';
import { FolderDocuments } from '@/types/documents';
import { getDocumentById } from '@/api/requests/documents';

interface TenderDocumentsSectionProps {
  tenderId: string;
  documents: Document[];
  isActiveTender: boolean;
}

const DocumentSection: React.FC<TenderDocumentsSectionProps> = ({ documents, isActiveTender, tenderId }) => {
  const getAllDocumentIdsWithSummaries = (): string[] => {
    return documents.filter((doc) => doc.summary).map((doc) => doc.id);
  };

  const { isAuthenticated, isLoading: authLoading } = useAuthenticated();
  const { hasAccessPermission, isLoading: accessLoading } = useHasTenderAccess(tenderId);
  const [openDocSummaries, setOpenDocSummaries] = useState<Set<string>>(
    new Set<string>(getAllDocumentIdsWithSummaries()),
  );
  const [expanded, setExpanded] = useState<boolean>(true);
  const [showAccessButton, setShowAccessButton] = useState<boolean>(false);
  const tree = useTree();

  const { rootDocuments, rootFolders } = useMemo(() => groupDocumentsIntoFolders(documents), [documents]);

  const isDownloadEnabled = isActiveTender && hasAccessPermission && isAuthenticated;

  useEffect(() => {
    tree.expandAllNodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isLoading = authLoading || accessLoading;

    if (isActiveTender && !isLoading) {
      setShowAccessButton(true);
    }
  }, [isActiveTender, authLoading, accessLoading]);

  const areAllSummariesOpen = (): boolean => {
    const docsWithSummaries = getAllDocumentIdsWithSummaries();
    return docsWithSummaries.length > 0 && docsWithSummaries.every((id) => openDocSummaries.has(id));
  };

  const toggleAllSummaries = () => {
    const allDocIdsWithSummaries = getAllDocumentIdsWithSummaries();

    if (areAllSummariesOpen()) {
      setOpenDocSummaries(new Set());
    } else {
      setOpenDocSummaries(new Set(allDocIdsWithSummaries));
    }
  };

  const toggleExpand = () => {
    if (expanded) {
      tree.collapseAllNodes();
    } else {
      tree.expandAllNodes();
    }
    setExpanded(!expanded);
  };

  const documentsWithSummaries = documents.filter((doc) => doc.summary).length;
  const allSummariesOpen = areAllSummariesOpen();
  const hasFolders = rootFolders.length > 0;

  const handleDocumentDownload = async (id: string, fileName: string, fileType: string) => {
    try {
      const response = await getDocumentById(id);
      const pdfBlob = new Blob([response], { type: fileType });

      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const renderDocumentNode = (doc: Document, parentPath: string, paddingLeft: string) => {
    const isOpen = openDocSummaries.has(doc.id);
    const hasSummary = !!doc.summary;
    const shouldShowSummary = isOpen && hasSummary;

    return {
      value: `${parentPath}${doc.name}`,
      label: (
        <div
          className={`relative grid w-full items-center gap-y-2 py-2 ${paddingLeft} pr-4 ${
            isDownloadEnabled ? 'cursor-pointer' : 'cursor-not-allowed'
          }`}
          onClick={() => {
            if (isDownloadEnabled) {
              handleDocumentDownload(doc.id, doc.name, doc.type);
            }
          }}
        >
          <div className="flex min-w-60 items-center gap-x-5 overflow-hidden font-normal">
            <FontAwesomeIcon icon={getIconByDocType(doc.type)} className={`fa-fw h-6 w-7`} />
            <div className="w-[calc(100%-44px)] flex-shrink-0 items-center overflow-hidden truncate" title={doc.name}>
              {doc.name}
            </div>
          </div>

          <div className="grid w-fit grid-cols-[auto_auto_auto] gap-x-3 pl-9">
            {doc.category && (
              <div className="flex w-fit items-center whitespace-nowrap rounded-full bg-base-100 px-2 py-1 text-sm font-light group-hover/treenode:bg-base-200">
                {DocumentCategory[doc.category as unknown as keyof typeof DocumentCategory]}
              </div>
            )}
            {doc.size && (
              <div className="flex w-fit items-center whitespace-nowrap rounded-full bg-base-100 px-2 py-1 text-sm font-light group-hover/treenode:bg-base-200">
                {formatFileSize(doc.size)}
              </div>
            )}
            {doc.parts && (
              <div className="flex w-fit items-center whitespace-nowrap rounded-full bg-base-100 px-2 py-1 text-sm font-light group-hover/treenode:bg-base-200">
                {doc.parts} {getFileContentTitle(doc.type, doc.parts)}
              </div>
            )}
          </div>

          {shouldShowSummary && <div className="pl-10 text-sm font-light text-primary">{doc.summary}</div>}
        </div>
      ),
    };
  };

  const buildTree = (folders: FolderDocuments[], docs: Document[], parentPath = ''): TreeNodeData[] => {
    const nodes: TreeNodeData[] = [];

    for (const folder of folders) {
      const path = parentPath + folder.folderName;
      const count = folder.documents.length + folder.subfolders.length;

      nodes.push({
        value: path,
        label: (
          <div className="flex w-full items-center justify-between py-2 pl-3 pr-4">
            <div className="flex flex-shrink-0 items-center gap-x-5 font-normal">
              <FontAwesomeIcon icon={faFolderClosed} className="fa-fw h-6 w-7" />
              <span title={folder.folderName} className="truncate">
                {folder.folderName}
              </span>
            </div>
            <div className="flex-shrink-0 text-right font-light">
              {count} Datei{count !== 1 ? 'en' : ''}
            </div>
          </div>
        ),
        children: [
          // subfolders
          ...buildTree(folder.subfolders, [], path + '/'),
          // files in this folder
          ...folder.documents.map((doc) => renderDocumentNode(doc, `${path}/`, 'pl-7')),
        ],
      });
    }

    // root-level docs
    nodes.push(...docs.map((doc) => renderDocumentNode(doc, parentPath, 'pl-8')));

    return nodes;
  };

  const treeData = useMemo(
    () => buildTree(rootFolders, rootDocuments),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rootFolders, rootDocuments, hasAccessPermission, openDocSummaries],
  );

  if (!documents.length) return null;

  return (
    <RoundedSectionCard>
      <div className="flex flex-col gap-3">
        {/* Title & Access Button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon className="fa-fw h-6 w-6" icon={faFileLines} />
            <h2 className="text-xl font-light">DOKUMENTE</h2>
          </div>
          {showAccessButton && (
            <div className="hidden md:block">
              <TenderAccessButton
                tenderId={tenderId}
                isAuthenticated={isAuthenticated}
                hasAccess={hasAccessPermission}
                label="Zum Downloadportal"
              />
            </div>
          )}
        </div>

        {/* Scrollable area with sticky column headers */}
        <div className="max-h-[32rem] overflow-x-auto overflow-y-auto">
          {/* Column headers */}
          <div className="sticky top-0 z-10 grid min-w-5xl items-center border-b border-[#939BA4] bg-base-200 py-2 pl-[70px] pr-4">
            <div className="flex items-center gap-3">
              {hasFolders && (
                <FontAwesomeIcon
                  className="fa-fw absolute left-0 h-6 w-6 cursor-pointer text-primary"
                  icon={expanded ? faArrowsToLine : faArrowsFromLine}
                  onClick={toggleExpand}
                />
              )}
              {documentsWithSummaries > 0 && (
                <button
                  onClick={toggleAllSummaries}
                  className="absolute left-8 flex items-center gap-1 rounded-md bg-base-200 py-1 "
                  title={allSummariesOpen ? 'Alle Zusammenfassungen ausblenden' : 'Alle Zusammenfassungen anzeigen'}
                >
                  <FontAwesomeIcon icon={allSummariesOpen ? faEyeSlash : faEye} className="fa-fw h-6 w-6" />
                </button>
              )}
              <span className="text-xl font-medium">Dateiname</span>
            </div>
          </div>

          {/* The Mantine Tree */}
          <Tree
            data={treeData}
            tree={tree}
            levelOffset={20}
            className="min-w-5xl"
            renderNode={({ node, expanded, hasChildren, elementProps, level }) => (
              <Group
                {...elementProps}
                style={{ ...elementProps.style, paddingLeft: `${(level - 1) * 20}px` }}
                className={`${hasChildren && 'flex items-center'} ${!hasChildren && hasAccessPermission && isAuthenticated && 'group/treenode cursor-pointer hover:bg-base-300'} border-b border-[#939BA4]`}
              >
                {hasChildren && (
                  <FontAwesomeIcon
                    icon={expanded ? faChevronDown : faChevronRight}
                    className="fa-fw h-6 w-7 cursor-pointer"
                  />
                )}
                {node.label}
              </Group>
            )}
          />
        </div>

        {isAuthenticated && (
          <div className="flex items-center gap-5">
            <FontAwesomeIcon icon={faCircleInfo} className="fa-fw h-6 w-7" />
            <span className="text-sm font-light">
              Für die verbindliche und aktuellste Fassung besuchen Sie bitte das Original-Portal.
            </span>
          </div>
        )}

        {/* Mobile access button */}
        {showAccessButton && (
          <div className="mt-4 md:hidden">
            <TenderAccessButton
              tenderId={tenderId}
              isAuthenticated={isAuthenticated}
              hasAccess={hasAccessPermission}
              label="Zum Downloadportal"
            />
          </div>
        )}
      </div>
    </RoundedSectionCard>
  );
};

export default DocumentSection;
